import { useState } from 'react'

import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import clsx from 'clsx'
import { observer } from 'mobx-react-lite'

import { isDifficultAviaRoute } from 'entities/ticket/lib/is-difficult-avia-route'
import findTicketFormStore from 'entities/ticket/models/find-ticket/find-ticket-store'
import { SelectedCountry } from 'entities/ticket/models/find-ticket/models'

import { ButtonContinue } from 'shared/ui/bottom-continue'
import { HeaderResetButton } from 'shared/ui/header-reset-button'

import { useFilterState } from '../filter-search-screen/hooks'

import { TimeCheckboxGroup, TimePeriod } from './components/time-checkbox-group'
import { convertPeriodsToTimeRange, convertTimeRangeToPeriods } from './lib'
import s from './styles.module.scss'

interface DirectionTimePeriods {
  departure: TimePeriod[]
}

const createEmptyDirectionPeriods = (length: number): DirectionTimePeriods[] =>
  Array.from({ length }, () => ({
    departure: [],
  }))

const FilterTimeScreenNew = observer(() => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const {
    state,
    setState,
    hasActiveFilters,
    hasChanges,
    onResetFields,
    onSubmit,
  } = useFilterState()

  if (!findTicketFormStore.formFindTicket?.directions?.length) return null

  const directions = findTicketFormStore.formFindTicket?.directions
  const isDifficultRoute = isDifficultAviaRoute(directions)

  const initializePeriodsFromFilter = (): DirectionTimePeriods[] => {
    const timeFly = state?.time
    if (!timeFly) {
      return createEmptyDirectionPeriods(directions?.length ?? 0)
    }

    return timeFly.map((timeFilter) => ({
      departure: convertTimeRangeToPeriods(timeFilter.departure),
    }))
  }

  const [directionsPeriods, setDirectionsPeriods] = useState<
    DirectionTimePeriods[]
  >(initializePeriodsFromFilter)

  const updateGlobalState = (updatedPeriods: DirectionTimePeriods[]) => {
    const timeFilters = updatedPeriods.map((direction) => {
      const depRange = convertPeriodsToTimeRange(direction.departure)

      if (depRange) {
        return {
          departure: depRange,
        }
      }

      return {}
    })

    const hasFilters = timeFilters.some((filter) => filter.departure)

    setState((prev) => ({
      ...prev,
      time: hasFilters ? timeFilters : undefined,
    }))
  }

  const onBack = () => {
    navigate(-1)
  }

  const onReset = () => {
    const emptyPeriods = createEmptyDirectionPeriods(directions?.length ?? 0)
    setDirectionsPeriods(emptyPeriods)
    onResetFields(['time'])
  }

  const onChangeDeparture = (index: number, periods: TimePeriod[]) => {
    const periodsCopy = [...directionsPeriods]
    if (!periodsCopy[index]) {
      periodsCopy[index] = { departure: [] }
    }
    periodsCopy[index].departure = periods
    setDirectionsPeriods(periodsCopy)
    updateGlobalState(periodsCopy)
  }

  const renderTimeCheckboxGroup = (
    index: number,
    first?: SelectedCountry,
    last?: SelectedCountry,
  ) => {
    const periods = directionsPeriods[index] || {
      departure: [],
    }

    return (
      <div
        className={s.block}
        key={index}
      >
        <TimeCheckboxGroup
          title={`${first?.cityName} - ${last?.cityName}`}
          selectedDeparturePeriods={periods.departure}
          onChangeDeparture={(p) => onChangeDeparture(index, p)}
        />
      </div>
    )
  }

  return (
    <div className={clsx('container', s.container)}>
      <HeaderResetButton
        isCanDisable={hasActiveFilters}
        title={t('time')}
        onBack={onBack}
        onReset={onReset}
      />

      <div className={s.content}>
        {isDifficultRoute ? (
          directions.map((direction, index) =>
            renderTimeCheckboxGroup(index, direction.from, direction.to),
          )
        ) : (
          <>
            {renderTimeCheckboxGroup(0, directions[0]?.from, directions[0]?.to)}

            {directions[0]?.back_date
              ? renderTimeCheckboxGroup(
                  1,
                  directions[0]?.to,
                  directions[0]?.from,
                )
              : null}
          </>
        )}
      </div>

      <ButtonContinue
        onClick={onSubmit}
        disabled={!hasChanges}
      >
        <span className={clsx('semi-bold-17', s.button)}>{t('apply')}</span>
      </ButtonContinue>
    </div>
  )
})

export default FilterTimeScreenNew
